import { GetExtranetOrderTicketGroupQueryDto } from '@pharmac-extranet/extranet-api'
import {
  ControlledDateRangePicker,
  ControlledInput,
  useBreakpoint,
  useLanguage,
} from '@pharmac-extranet/shared'
import { PharmacButton } from '@pharmac/ui'
import { useEffect, useState } from 'react'
import { Control, FieldValues, FormProvider, useForm } from 'react-hook-form'
import { createSearchParams, useSearchParams } from 'react-router-dom'
import { cleanQueryParams } from '../../features/infinite-query/util/cleanQueryParams'
import {
  ExtranetFiltersColumn,
  ExtranetFiltersFooter,
  ExtranetFiltersRow,
} from '../filters/ExtranetFiltersComponents'
import { mapOrderTicketParamsToQueryParams } from './util/query-parameter-mapping'

type Props = {
  closeFilters: () => void
}

export const OrderTicketFilters = ({ closeFilters }: Props) => {
  const { t } = useLanguage()
  const { minDesktop } = useBreakpoint()

  const [searchParams, setSearchParams] = useSearchParams()
  const [filters, setFilters] = useState(mapOrderTicketParamsToQueryParams(searchParams))

  const formhook = useForm<GetExtranetOrderTicketGroupQueryDto>({
    mode: 'onChange',
    defaultValues: {
      startDeadlineRange: filters.startDeadlineRange ?? '',
      endDeadlineRange: filters.endDeadlineRange ?? '',
      wholeName: filters.wholeName ?? '',
    },
  })

  useEffect(() => {
    const params = mapOrderTicketParamsToQueryParams(searchParams)

    setFilters(params)

    formhook.reset({
      startDeadlineRange: params.startDeadlineRange ?? '',
      endDeadlineRange: params.endDeadlineRange ?? '',
      wholeName: params.wholeName ?? '',
    })
  }, [searchParams])

  const handleSubmit = (values: GetExtranetOrderTicketGroupQueryDto) => {
    setSearchParams(
      createSearchParams(
        cleanQueryParams({
          ...filters,
          ...values,
        })
      )
    )
    closeFilters()
  }

  return (
    <FormProvider {...formhook}>
      <ExtranetFiltersRow style={{ height: minDesktop ? '200px' : undefined }}>
        <ExtranetFiltersColumn>
          <ControlledDateRangePicker
            control={formhook.control as unknown as Control<FieldValues>}
            label={t('extranet:customerGroupSettings.orderTime')}
            startDateName="startDeadlineRange"
            endDateName="endDeadlineRange"
            startDateText={t('startDateText', { ns: 'common' })}
            endDateText={t('endDateText', { ns: 'common' })}
            placeholder={t('selectDateRange', { ns: 'common' })}
            weekText={t('week', { ns: 'common' })}
            calendarPlacement="bottom"
          />
        </ExtranetFiltersColumn>
        <ExtranetFiltersColumn>
          <ControlledInput
            label={t('extranet:correctionRequests.customerName')}
            name="wholeName"
            control={formhook.control as unknown as Control<FieldValues>}
            placeholder={t('extranet:correctionRequests.searchByName')}
          />
        </ExtranetFiltersColumn>
      </ExtranetFiltersRow>
      <ExtranetFiltersFooter>
        <PharmacButton
          onClick={formhook.handleSubmit(handleSubmit)}
          disabled={!formhook.formState.isDirty}
        >
          {t('search', { ns: 'common' })}
        </PharmacButton>
      </ExtranetFiltersFooter>
    </FormProvider>
  )
}
